import type { Uid } from "@/helpers/utility/Id";
import { defineItemStore } from "../itemStore.store";
import { defaultChartSettings, defaultData, defaultDataToggles, defaultItemConfigs } from "./ChartItem";
import { addItems, addSimulation, addSimulationSymbol, clearItemsOfType, removeItems, removeSimulations, updateAxis, updateChartType, updateRoundsToSimulations } from "./chartStoreFunctions";
import type { UnfilledSymbolName } from "@core/ui/svg-icons/symbols/SymbolName";
import type { Subset } from "@/helpers/utility/Type";
import { assignDeep } from "@/helpers/utility/CollectionFunctions";
import { cloneDeep } from "lodash-es";
import { useSimulationStore } from "../simulation/simulations.store";
import type {
  ChartType,
  ChartItem,
  ChartSettings,
  ConfigurableType,
  ItemConfig,
  SelectableChartItem,
  SelectableChartItemType
} from "./ChartItem";

const chartItemDefaults: ChartItem = Object.freeze({
  type: "chart",
  
  chartType: "bar",

  data: { ...defaultData },
  dataToggles: { ...defaultDataToggles },
  itemConfigs: { ...defaultItemConfigs },
  chartSettings: { ...defaultChartSettings },
  
  id: 0,
  uid: "chart_0",
  createdAt: 0
});

export const useChartStore = defineItemStore(
  "model/charts",
  chartItemDefaults,
  {
    state: (baseStoreState) => {
      return {
        ...baseStoreState,

        defaultChartSettings: { ...defaultChartSettings },
        defaultChartType: "line" as ChartType,
        lastUpdatedItemId: 0
      };
    },

    actions: {
      updateChartSettings(
        evaluationId: number,
        data: Partial<ChartSettings>
      ): void {
        const chart = this.items[evaluationId];
        const chartSettings = chart.chartSettings;
        Object.assign(chartSettings, data);

        this.defaultChartSettings = {
          ...chartSettings
        };

        this.lastUpdatedItemId = evaluationId;
      },

      setChartType(id: number, newChartType: ChartType): void {
        const item = this.items[id];
        updateChartType(item, newChartType);
      },
      setAxis(id: number, axis: "x" | "y", newAxis: string): void {
        const item = this.items[id];
        updateAxis(item, axis, newAxis);
      },

      /**
       * Select the main simulation of an evaluation.
       */
      selectSimulation(
        evaluationId: number,
        simulationId?: number | null
      ): void {
        const chart = this.items[evaluationId];
        chart.data.selectedSimulationId = simulationId ?? undefined;

        console.log("selected simulation", simulationId);

        // make sure the rounds are not out of bounds:
        updateRoundsToSimulations(chart);
      },
      selectItem(id: number, item: SelectableChartItem | null): void {
        const chart = this.items[id];

        chart.data.selectedItem = item
          ? ({ id: item.id, type: item.type } as SelectableChartItem)
          : undefined;

        console.log(
          "charts data",
          this.orderedItems.map((c) => ({
            selectedItem: c.data.selectedItem,
            id: c.id
          }))
        );
      },
      toggleAllOption(
        chartId: number,
        type: "target" | "resource" | "index" | "simulationRound" | "simulation"
      ): boolean {
        const chart = this.items[chartId];
        const value = !chart.dataToggles.all[type];
        chart.dataToggles.all[type] = value;

        return value;
      },

      addItems(
        chartId: number,
        itemIds: Uid<"target" | "resource" | "index">[]
      ): void {
        const chart = this.items[chartId];
        addItems(chart, itemIds);
      },
      removeItems(
        chartId: number,
        itemIds: Uid<"target" | "resource" | "index">[]
      ): void {
        const chart = this.items[chartId];
        removeItems(chart, itemIds);
      },
      clearItemsOfType(
        chartId: number,
        type: "target" | "resource" | "index"
      ): void {
        const chart = this.items[chartId];
        clearItemsOfType(chart, type);
      },

      updateItemOrder<T extends SelectableChartItemType>(
        chartId: number,
        itemType: T,
        order: SelectableChartItem<T>["id"][]
      ): void {
        const chart = this.items[chartId];

        chart.data[itemType] = order.slice() as (typeof chart.data)[T];
      },

      addSimulation(chartId: number, simulationId: number): void {
        const chart = this.items[chartId];
        addSimulation(chart, simulationId);
      },
      removeSimulations(chartId: number, simulationIds: number[]): void {
        const chart = this.items[chartId];
        removeSimulations(chart, simulationIds);
      },
      clearSimulations(chartId: number): void {
        const chart = this.items[chartId];
        chart.dataToggles.all.simulation = false;
        chart.data.simulation = [];

        updateRoundsToSimulations(chart);
      },

      // ==== ROUNDS ====
      setRounds(
        chartId: number,
        roundIndices: number[],
        options = { all: false }
      ): void {
        const chart = this.items[chartId];
        const chartData = chart.data;

        chart.dataToggles.all.simulationRound = options.all;
        chartData.simulationRound = [];
        chartData.simulationRound.push(...roundIndices);
      },
      addRounds(chartId: number, roundIndices: number[]): void {
        const chart = this.items[chartId];
        const chartData = chart.data;

        chart.dataToggles.all.simulationRound = false;
        chartData.simulationRound.push(...roundIndices);
      },
      removeRounds(chartId: number, roundIndices: number[]): void {
        const chart = this.items[chartId];
        const chartData = chart.data;

        chart.dataToggles.all.simulationRound = false;

        const ids = chartData.simulationRound;
        for (const roundIndex of roundIndices) {
          const index = ids.indexOf(roundIndex);
          if (index < 0) continue;
          ids.splice(index, 1);
        }
      },

      addActionActivation(chartId: number, actionActivationId: string): void {
        const chart = this.items[chartId];

        const ids = chart.data.actionActivation;

        if (ids.includes(actionActivationId)) return;

        ids.push(actionActivationId);
      },
      removeActionActivation(
        chartId: number,
        actionActivationId: string
      ): void {
        const chart = this.items[chartId];

        const ids = chart.data.actionActivation;

        const index = ids.indexOf(actionActivationId);
        if (index == -1) return;

        ids.splice(index, 1);
      },

      addIncidentActivation(
        chartId: number,
        incidentActivationId: string
      ): void {
        const chart = this.items[chartId];

        const ids = chart.data.incidentActivation;

        if (ids.includes(incidentActivationId)) return;

        ids.push(incidentActivationId);
      },
      removeIncidentActivation(
        chartId: number,
        incidentActivationId: string
      ): void {
        const chart = this.items[chartId];

        const ids = chart.data.incidentActivation;

        const index = ids.indexOf(incidentActivationId);
        if (index == -1) return;

        ids.splice(index, 1);
      },

      // ==== ITEM CONFIGURATION ====

      /**
       * Get an item configuration, making sure it exists.
       * @param chartId
       * @param itemType
       * @param itemId
       */
      getOrCreateItemConfig(
        chartId: number,
        itemType: ConfigurableType,
        itemId: number | string,
        subgroup?: string
      ): ItemConfig {
        const chart = this.items[chartId];

        if (!(itemType in chart.itemConfigs)) {
          chart.itemConfigs[itemType] = {};
        }
        if (!(itemId in chart.itemConfigs[itemType])) {
          chart.itemConfigs[itemType][itemId] = {
            open: false
          };
        }
        const itemConfig = chart.itemConfigs[itemType][itemId];
        if (!subgroup) return itemConfig;

        if (!itemConfig.subgroups) itemConfig.subgroups = {};
        if (!itemConfig.subgroups[subgroup]) {
          itemConfig.subgroups[subgroup] = {
            open: false
          };
        }
        return itemConfig.subgroups[subgroup];
      },

      updateItemConfig(
        chartId: number,
        itemType: ConfigurableType,
        itemId: number | string,
        subgroup: string | undefined,
        changes: object = {}
      ): void {
        const config = this.getOrCreateItemConfig(
          chartId,
          itemType,
          itemId,
          subgroup
        );
        Object.assign(config, changes);
      },

      setItemSymbol(
        chartId: number,
        itemType: ConfigurableType,
        itemId: number | string,
        symbol: UnfilledSymbolName,
        subgroup?: string
      ): void {
        const config = this.getOrCreateItemConfig(
          chartId,
          itemType,
          itemId,
          subgroup
        );
        config.symbol = symbol;

        this.lastUpdatedItemId = chartId;
      },

      setItemColor(
        chartId: number,
        itemType: ConfigurableType,
        itemId: number | string,
        color: string | null,
        simulationId?: number,
        subgroup?: string
      ): void {
        const config = this.getOrCreateItemConfig(
          chartId,
          itemType,
          itemId,
          subgroup
        );
        if (simulationId != null) {
          if (!(simulationId in config)) config[simulationId] = {};
          config[simulationId].color = color;
        } else {
          config.color = color;
        }

        this.lastUpdatedItemId = chartId;
      },

      closeItemConfig(
        chartId: number,
        itemType: ConfigurableType,
        itemId: number | string,
        subgroup: string | undefined,
        options: { color?: string | null } = {}
      ): void {
        const config = this.getOrCreateItemConfig(
          chartId,
          itemType,
          itemId,
          subgroup
        );
        Object.assign(config, options);
        config.open = false;

        // remove all simulation-specific configs
        for (const prop in config) {
          if (!isNaN(parseInt(prop))) {
            delete config[prop];
          }
        }

        this.lastUpdatedItemId = chartId;
      },

      async duplicate(
        id: number,
        changes: Partial<ChartItem> = {}
      ): Promise<ChartItem> {
        const newChart = await this.super.duplicate(id, changes);

        return newChart;
      }
    },
    events: {
      async beforeCreate(chartItem) {
        const changes: Subset<ChartItem> = {};

        // Copy the item configuration of the last edited chart
        const lastChart = this.items[this.lastUpdatedItemId];
        if (lastChart) {
          const itemConfigs = chartItem.itemConfigs || {};
          const extendedItemConfig = assignDeep(
            cloneDeep(lastChart.itemConfigs),
            itemConfigs
          );
          changes.itemConfigs = extendedItemConfig;
        }

        // make sure all simulations used in the chart have a symbol
        // assigned
        let simulationIds = (chartItem.data?.simulation || []) as number[];
        if (chartItem.dataToggles?.all?.simulation) {
          simulationIds = [...useSimulationStore().ids];
        }
        for (const simulationId of simulationIds) {
          addSimulationSymbol(
            changes?.itemConfigs?.simulation || {},
            simulationId
          );
        }

        changes.data = {
          ...cloneDeep(chartItemDefaults.data),
          ...chartItem.data
        };
        
        changes.chartSettings = {
          ...cloneDeep(this.defaultChartSettings),
          ...chartItem.chartSettings
        };

        return { ...chartItem, ...changes };
      },

      afterUpdate(chartItem, updatedData) {
        this.lastUpdatedItemId = chartItem.id;

        // Update default chart settings
        const data = updatedData;
        if (data?.chartSettings) {
          this.defaultChartSettings = {
            ...this.defaultChartSettings,
            ...data.chartSettings
          };
        }
        
        if (data?.chartType != null) {
          this.defaultChartType = data.chartType;
        }
      }
    }
  }
);